import React from 'react';
import { useDispatch } from 'react-redux';
import { OverlayConstants } from '../../../Constants';
import { setOverlay } from '../../../redux/slices/appStateSlice';
import {
  Image,
  isEditorActive,
  Placeholder,
  RichText,
  Text,
} from '@sitecore-jss/sitecore-jss-react';
import JavaScriptLink from '../../../utils/navigate/JavaScriptLink';
import PropTypes from 'prop-types';

const WideCard = ({ fields, params, rendering }) => {
  const dispatch = useDispatch();
  const { variant, imageOnRight, isStacked, innerContainer, useConditionalRichText } = params || {};

  // Check if the innerContainer is false, and return true or false accordingly
  const showInnerContainer = innerContainer !== 'false';

  const showCallback = () => {
    dispatch(setOverlay({ type: OverlayConstants.Callback }));
  };

  const conditionalPrimaryCta = () => {
    if (fields.primaryCta?.value?.text) {
      return (
        <JavaScriptLink
          className="vds-button vds-wide-card__button"
          field={fields.primaryCta}
          data-testid={`${fields.testid?.value}-primarycta`}
        />
      );
    }
    return (
      <button
        onClick={showCallback}
        type="button"
        className="vds-button vds-wide-card__button"
        data-testid={`${fields.testid?.value}-callback-button`}
      >
        <Text
          tag="span"
          className="vds-global-navigation__back-text"
          field={fields.callBackButtonText}
        />
      </button>
    );
  };

  const getCardClassName = () => {
    const classes = ['vds-wide-card'];
    if (variant && variant.toLowerCase() !== 'white') {
      classes.push(`vds-wide-card--${variant}`);
    }
    if (imageOnRight) {
      classes.push('vds-wide-card--image-right');
    }
    if (isStacked) {
      classes.push('vds-wide-card--stacked');
    }
    return classes.join(' ');
  };

  const shouldDisplayCtas =
    isEditorActive() ||
    fields.primaryCta?.value?.text ||
    fields.callBackButtonText?.value ||
    fields.secondaryCta?.value?.text;

  const cardContent = (
    <div className={getCardClassName()} data-testid={`${fields.testid?.value}-cardcontent`}>
      <div className="vds-wide-card__source-image-container">
        <Image
          className="vds-wide-card__source-image"
          field={fields.image}
          data-testid={`${fields.testid?.value}-image`}
        />
      </div>

      <div className="vds-wide-card__text-container">
        <div className="vds-wide-card__text">
          <Text
            tag="h2"
            className="vds-wide-card__heading"
            field={fields.heading}
            data-testid={`${fields.testid?.value}-heading`}
          />
          {useConditionalRichText === '1' ? (
            <Placeholder name="widecard-content" rendering={rendering} />
          ) : (
            <RichText
              tag="p"
              field={fields.content}
              data-testid={`${fields.testid?.value}-content`}
            />
          )}
          {shouldDisplayCtas && (
            <div className="vds-wide-card__button-container">
              <div className="vds-wide-card__button-container-inner">
                {conditionalPrimaryCta()}
                {(isEditorActive() || fields.secondaryCta?.value?.text) && (
                  <JavaScriptLink
                    className="vds-button vds-button--line vds-wide-card__button"
                    field={fields.secondaryCta}
                    data-testid={`${fields.testid?.value}-secondarycta`}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {showInnerContainer === false ? (
        cardContent
      ) : (
        <div
          className="vds-page-container__section"
          data-testid={`${fields.testid?.value}-container-section`}
        >
          <div
            className="vds-page-container__inner"
            data-testid={`${fields.testid?.value}-container-inner`}
          >
            {cardContent}
          </div>
        </div>
      )}
    </>
  );
};

WideCard.propTypes = {
  fields: PropTypes.shape({
    testid: PropTypes.shape({ value: PropTypes.string }),
    primaryCta: PropTypes.shape({ value: PropTypes.shape({ text: PropTypes.string }) }),
    secondaryCta: PropTypes.shape({ value: PropTypes.shape({ text: PropTypes.string }) }),
    callBackButtonText: PropTypes.shape({ value: PropTypes.string }),
    image: PropTypes.shape({ value: PropTypes.object }),
    heading: PropTypes.shape({ value: PropTypes.string }),
    content: PropTypes.shape({ value: PropTypes.string }),
  }).isRequired,
  params: PropTypes.shape({
    variant: PropTypes.string,
    imageOnRight: PropTypes.string,
    isStacked: PropTypes.string,
    innerContainer: PropTypes.string,
    useConditionalRichText: PropTypes.string,
  }),
  rendering: PropTypes.object,
};

export default WideCard;
